import { useId, useRef, useState } from 'react';
import { groupTripApi } from '../../api/groupTripApi';
import {
  buildContinuationRequest,
  createContinuationRequestId,
  createErrorPresentation,
  formatActualDateTime,
  TITLE_MAX_LENGTH,
  toDateTimeInputValue,
  validateContinuationDraft,
} from './actualContinuationModel';

const EMPTY_DRAFT = Object.freeze({ title: '', continuationStartedAt: '' });

/**
 * 建立個人後續行程。開始時間必須由使用者明確選擇，不預填任何時間。
 * 同一次送出沿用同一個 clientRequestId；結果不明時鎖定內容並以相同請求重試。
 */
export default function PersonalContinuationForm({
  groupTripId, travelerId, effectiveJoinAt, onCreated, onAlreadyExists, onCancel,
}) {
  const formId = useId();
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [pending, setPending] = useState(false);
  const requestIdRef = useRef(null);
  const resetKeyOnEditRef = useRef(false);
  const uncertainRequestRef = useRef(null);
  const sendingRef = useRef(false);
  const locked = submitError?.kind === 'uncertain';
  const minimum = effectiveJoinAt ? toDateTimeInputValue(effectiveJoinAt) : '';

  function change(field, value) {
    if (locked) return;
    if (resetKeyOnEditRef.current) {
      requestIdRef.current = null;
      resetKeyOnEditRef.current = false;
    }
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  }

  function editAfterUncertain() {
    uncertainRequestRef.current = null;
    requestIdRef.current = null;
    setSubmitError(null);
  }

  async function submit(event) {
    event.preventDefault();
    if (sendingRef.current) return;
    let request = uncertainRequestRef.current;
    if (!request) {
      const nextErrors = validateContinuationDraft(draft, effectiveJoinAt);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;
      if (!requestIdRef.current) requestIdRef.current = createContinuationRequestId();
      request = buildContinuationRequest(draft, requestIdRef.current);
    }
    sendingRef.current = true;
    setPending(true);
    setSubmitError(null);
    try {
      const result = await groupTripApi.createPersonalContinuation(groupTripId, travelerId, request);
      uncertainRequestRef.current = null;
      onCreated(result);
    } catch (cause) {
      const presentation = createErrorPresentation(cause);
      if (presentation.kind === 'uncertain') {
        uncertainRequestRef.current = request;
        setSubmitError(presentation);
      } else if (presentation.kind === 'already-exists') {
        uncertainRequestRef.current = null;
        onAlreadyExists(presentation);
      } else {
        uncertainRequestRef.current = null;
        if (presentation.kind === 'conflict') requestIdRef.current = null;
        else resetKeyOnEditRef.current = true;
        setSubmitError(presentation);
      }
    } finally {
      sendingRef.current = false;
      setPending(false);
    }
  }

  const titleErrorId = `${formId}-title-error`;
  const titleHintId = `${formId}-title-hint`;
  const startErrorId = `${formId}-start-error`;
  const startHintId = `${formId}-start-hint`;
  const submitErrorId = `${formId}-submit-error`;

  return (
    <form className="actual-view__form" onSubmit={submit} noValidate aria-describedby={submitError ? submitErrorId : undefined} data-testid="continuation-form">
      <ul className="actual-view__confirm" data-testid="continuation-confirm-copy">
        <li>這會建立一份屬於你的獨立個人行程。</li>
        <li>不會修改這趟揪團，也不會影響其他旅伴。</li>
        <li>不會自動複製揪團的行程內容，新行程從空白開始，之後也不會與揪團同步。</li>
        <li>新行程不會自動公開，是否分享由你另外決定。</li>
      </ul>
      <div className="actual-view__field">
        <label htmlFor={`${formId}-title`}>行程名稱</label>
        <input
          id={`${formId}-title`}
          name="title"
          type="text"
          value={draft.title}
          disabled={pending || locked}
          aria-invalid={errors.title ? 'true' : 'false'}
          aria-describedby={[titleHintId, errors.title ? titleErrorId : ''].filter(Boolean).join(' ')}
          required
          onChange={(event) => change('title', event.target.value)}
        />
        <span id={titleHintId} className="actual-view__form-hint">{`1 至 ${TITLE_MAX_LENGTH} 字`}</span>
        {errors.title ? <p id={titleErrorId} className="actual-view__field-error">{errors.title}</p> : null}
      </div>
      <div className="actual-view__field">
        <label htmlFor={`${formId}-start`}>個人行程開始時間</label>
        <input
          id={`${formId}-start`}
          name="continuationStartedAt"
          type="datetime-local"
          value={draft.continuationStartedAt}
          min={minimum || undefined}
          disabled={pending || locked}
          aria-invalid={errors.continuationStartedAt ? 'true' : 'false'}
          aria-describedby={[minimum ? startHintId : '', errors.continuationStartedAt ? startErrorId : '']
            .filter(Boolean).join(' ') || undefined}
          required
          onChange={(event) => change('continuationStartedAt', event.target.value)}
        />
        {minimum ? (
          <span id={startHintId} className="actual-view__form-hint" data-testid="continuation-lower-bound">
            {`不可早於你加入這趟揪團的時間（${formatActualDateTime(effectiveJoinAt)}）`}
          </span>
        ) : null}
        {errors.continuationStartedAt ? (
          <p id={startErrorId} className="actual-view__field-error">{errors.continuationStartedAt}</p>
        ) : null}
      </div>
      {submitError ? (
        <p id={submitErrorId} className="actual-view__error" role="alert" data-error-kind={submitError.kind}>
          {submitError.message}
        </p>
      ) : null}
      <div className="actual-view__actions">
        <button type="submit" disabled={pending}>
          {locked ? '以相同內容重試' : '確認建立個人後續行程'}
        </button>
        {locked ? (
          <button type="button" className="actual-view__secondary-button" disabled={pending} onClick={editAfterUncertain}>
            修改內容
          </button>
        ) : null}
        <button type="button" className="actual-view__secondary-button" disabled={pending} onClick={onCancel}>
          取消
        </button>
      </div>
    </form>
  );
}
